/* eslint-disable no-console */
import { getRequest, putRequest, postRequest, deleteRequest } from '../../../../../../api/http-v2';

// Space Video Embeds API Calls (For Initial Space Creation)

/**
 * Gets all video/3D tour records associated with a Space.
 * @param {string} spaceId The ID of the space.
 * @returns {Promise<object>} A promise that resolves with an array of media records.
 */
export async function getSpaceEmbedMedia (spaceId) {
  const endpoint = `/v2/spaces-v2/spaces/${spaceId}/embed-media`;
  const response = await getRequest({
    endpoint,
    options: { useAccessToken: true }
  });
  return response.data;
}

/**
 * Fetches core image records and metadata associated with a specific Space.
 * This is typically used to get the initial image data for the space during creation.
 * @param {string} spaceId The ID of the space.
 * @returns {Promise<object>} A promise that resolves with the space's image data object.
 */
export async function getSpaceImages (spaceId) {
  const endpoint = `/v1/spaces/${spaceId}`;
  const response = await getRequest({
    endpoint,
    options: { useAccessToken: true }
  });
  return response.data;
}

/**
 * Creates a new image record associated with a Space.
 * @param {object} newSpaceImage - The data for the new image (URL, parentId, etc.).
 * @returns {Promise<object>} The newly created image data.
 */
export async function createSpaceImage (newSpaceImage) {
  const endpoint = '/v1/spaces/images/';
  const body = newSpaceImage;
  const query = {
    timestamp: Date.now()
  };
  return postRequest({ endpoint, body, query, options: { useAccessToken: true } });
}

/**
 * Updates the metadata or sort order of an existing space image.
 * @param {object} spaceImage - Object containing image ID and payload for the update.
 * @returns {Promise<object>} The updated image data.
 */
export async function updateSpaceImage (spaceImage) {
  const endpoint = `/v1/spaces/images/${spaceImage.id}`;
  const body = spaceImage.payload;

  return putRequest({ endpoint, body, options: { useAccessToken: true } });
}

/**
 * Deletes a specific image record from a Space.
 * @param {string} spaceImageId - The ID of the image to delete.
 * @returns {Promise<object>} Success confirmation response.
 */
export async function deleteSpaceImage (spaceImageId) {
  const endpoint = `/v1/spaces/images/${spaceImageId}`;

  return deleteRequest({ endpoint, options: { useAccessToken: true } });
}

/**
 * Creates new video/3D tour records associated with a Space.
 * @param {string} spaceId The ID of the space.
 * @param {Array<object>} mediaPayload Array of objects: [{ url, sort_order }]
 * @returns {Promise<object>} A promise that resolves with the created media objects (including IDs).
 */
export async function createSpaceEmbedMedia (spaceId, mediaPayload) {
  const endpoint = `/v2/spaces-v2/spaces/${spaceId}/embed-media`;
  const response = await postRequest({ endpoint, body: mediaPayload, options: { useAccessToken: true } });
  return response.data;
}

/**
 * Updates the sort order of existing video/3D tour records on a Space.
 * @param {string} spaceId The ID of the space.
 * @param {Array<object>} orderPayload Array of objects: [{ id, sort_order }]
 * @returns {Promise<object>} A promise that resolves with the updated media objects.
 */
export async function updateSpaceEmbedMediaOrder (spaceId, orderPayload) {
  const endpoint = `/v2/spaces-v2/spaces/${spaceId}/embed-media`;
  return putRequest({ endpoint, body: orderPayload, options: { useAccessToken: true } });
}

/**
 * Deletes a specific video/3D tour record from a Space.
 * @param {string} spaceId The ID of the space.
 * @param {string} mediaId The ID of the video/3D tour record to delete.
 * @returns {Promise<object>} A promise that resolves upon successful deletion.
 */
export async function deleteSpaceEmbedMedia (spaceId, mediaId) {
  const endpoint = `/v2/spaces-v2/spaces/${spaceId}/embed-media/${mediaId}`;
  return deleteRequest({ endpoint, options: { useAccessToken: true } });
}
